import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Quote, ChevronLeft, ChevronRight, DollarSign, Users, ShoppingBag, Home, Utensils, GraduationCap, Baby, HandHeart, Send, Camera, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

// Default stories (shown if no approved stories from DB)
const defaultStories = [
  {
    id: 1,
    name: "Maria & Family",
    image: "👨‍👩‍👧‍👦",
    location: "Kissimmee",
    story: "When I lost my job during the pandemic, I didn't know how I would feed my three children. Living Fruit Ministry not only provided groceries every week but also prayed with us and helped me find job training. Today, I have a new career and volunteer at the food pantry to give back. This ministry saved my family.",
    date: "2023",
    category: "Food Assistance"
  },
  {
    id: 2,
    name: "James",
    image: "👨",
    location: "Orlando",
    story: "After being released from prison, I had nothing. No family would talk to me, no employer would hire me. Pastor Liza and the Living Fruit family welcomed me without judgment. They helped me get back on my feet, taught me about God's forgiveness, and now I lead a small group. I found more than food here — I found redemption.",
    date: "2022",
    category: "Life Transformation"
  },
  {
    id: 3,
    name: "The Rodriguez Family",
    image: "👨‍👩‍👧",
    location: "St. Cloud",
    story: "We immigrated from Venezuela with nothing but hope. Living Fruit Ministry helped us with food, clothing, and even furniture for our apartment. But what meant the most was being included in the church family. Our daughter now participates in youth programs, and we finally feel like we belong somewhere.",
    date: "2024",
    category: "New Beginnings"
  },
  {
    id: 4,
    name: "Dorothy",
    image: "👵",
    location: "Kissimmee",
    story: "At 78 years old, living on a fixed income, I often had to choose between medicine and food. The ministry's senior food program has been a blessing. Every week, volunteers deliver groceries right to my door with a smile and a prayer. I don't feel forgotten anymore.",
    date: "2024",
    category: "Senior Care"
  },
  {
    id: 5,
    name: "Michael & Sons",
    image: "👨‍👦‍👦",
    location: "Poinciana",
    story: "As a single father of two boys after my wife passed, I was drowning. The grief counseling, meal support, and genuine care from this ministry helped us survive the darkest time. My boys now have positive role models in the men's group, and I've learned that asking for help isn't weakness — it's wisdom.",
    date: "2023",
    category: "Family Support"
  }
];

// Story categories for the form
const storyCategories = [
  "Food Assistance",
  "Life Transformation", 
  "New Beginnings",
  "Senior Care",
  "Family Support",
  "Youth & Children",
  "Spiritual Growth",
  "Community Support",
  "Other"
];

// Impact Calculator Data
const impactLevels = [
  { 
    amount: 25, 
    icon: Utensils, 
    title: "Feed a Family", 
    description: "Provides a week of groceries for a family of four",
    color: "bg-green-500"
  },
  { 
    amount: 50, 
    icon: ShoppingBag, 
    title: "Stock the Pantry", 
    description: "Supplies groceries for 2 families in need",
    color: "bg-blue-500"
  },
  { 
    amount: 100, 
    icon: Users, 
    title: "Community Meal", 
    description: "Sponsors a community meal serving 20+ people",
    color: "bg-purple-500"
  },
  { 
    amount: 250, 
    icon: Baby, 
    title: "Baby Essentials", 
    description: "Provides diapers, formula & supplies for new parents for 1 month",
    color: "bg-pink-500"
  },
  { 
    amount: 500, 
    icon: Home, 
    title: "Crisis Support", 
    description: "Helps a family facing eviction with rent assistance",
    color: "bg-orange-500"
  },
  { 
    amount: 1000, 
    icon: GraduationCap, 
    title: "Transform a Life", 
    description: "Funds job training & support for someone rebuilding their life",
    color: "bg-gold"
  }
];

export default function ImpactStoriesPage() {
  const [currentStory, setCurrentStory] = useState(0);
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [impactStories, setImpactStories] = useState(defaultStories);
  
  // Share Your Story form state
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [storyFormData, setStoryFormData] = useState({
    name: '',
    location: '',
    category: '',
    story: '',
    email: '',
    phone: ''
  });
  const [storyPhoto, setStoryPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Fetch approved stories from database
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stories/approved`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            // Map DB stories to display format
            const dbStories = data.map(s => ({
              id: s.id,
              name: s.name,
              image: s.image_url || getDefaultEmoji(s.category),
              location: s.location,
              story: s.story,
              date: s.date,
              category: s.category
            }));
            setImpactStories(dbStories);
          }
        }
      } catch (error) {
        console.log('Using default stories');
      }
    };
    fetchStories();
  }, []);

  // Helper to get default emoji based on category
  const getDefaultEmoji = (category) => {
    const emojiMap = {
      'Food Assistance': '🍞',
      'Life Transformation': '🙏',
      'New Beginnings': '🌱',
      'Senior Care': '👵',
      'Family Support': '👨‍👩‍👧‍👦',
      'Youth & Children': '👧',
      'Spiritual Growth': '✝️',
      'Community Support': '🤝'
    };
    return emojiMap[category] || '💚';
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setStoryPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleStorySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', storyFormData.name);
      formData.append('location', storyFormData.location);
      formData.append('category', storyFormData.category);
      formData.append('story', storyFormData.story);
      if (storyFormData.email) formData.append('email', storyFormData.email);
      if (storyFormData.phone) formData.append('phone', storyFormData.phone);
      if (storyPhoto) formData.append('photo', storyPhoto);

      const response = await fetch(`${API_URL}/api/stories/submit`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Thank you for sharing your story!');
      } else {
        toast.error(data.detail || 'Failed to submit story');
      }
    } catch (error) {
      toast.error('Failed to submit story. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStoryFormData({ name: '', location: '', category: '', story: '', email: '', phone: '' });
    setStoryPhoto(null);
    setPhotoPreview(null);
    setSubmitted(false);
    setShowStoryForm(false);
  };

  const nextStory = () => {
    setCurrentStory((prev) => (prev + 1) % impactStories.length);
  };

  const prevStory = () => {
    setCurrentStory((prev) => (prev - 1 + impactStories.length) % impactStories.length);
  };

  const story = impactStories[currentStory];

  // Calculate impact for custom amounts
  const getImpactDescription = (amount) => {
    const families = Math.floor(amount / 25);
    const meals = families * 21; // 3 meals x 7 days
    return { families, meals };
  };

  const displayAmount = customAmount ? parseInt(customAmount) || 0 : selectedAmount;
  const impact = getImpactDescription(displayAmount);

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="impact-stories-page">
      {/* Ministry Banner */}
      <div className="w-full">
        <img 
          src={MINISTRY_BANNER}
          alt="Living Fruit Ministry"
          className="w-full h-auto object-cover"
          style={{ maxHeight: '300px', objectPosition: 'center' }}
        />
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-cream to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1 rounded-full text-xs uppercase tracking-widest mb-4"
              style={{ background: '#f3e8d8', color: '#b06820', fontWeight: 600 }}>
              Real Stories, Real Impact
            </span>
            <h1 className="font-serif text-4xl md:text-5xl mb-4" style={{ color: '#1A1A1A' }}>
              Lives Changed Through <em className="italic" style={{ color: '#c07030' }}>Your</em> Generosity
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#1E3A5F' }}>
              Every donation, every prayer, every act of kindness creates ripples of hope. 
              Here are the faces and stories behind the numbers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Stories Carousel */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Story Card */}
            <div className="bg-gradient-to-br from-cream to-white rounded-3xl p-8 md:p-12 shadow-xl border border-gold/10">
              {/* Quote Icon */}
              <div className="absolute -top-6 left-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#c07030' }}>
                  <Quote className="text-white" size={24} />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center text-5xl md:text-6xl"
                    style={{ background: '#f3e8d8' }}>
                    {story.image}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1 rounded-full text-xs font-medium"
                      style={{ background: '#c07030', color: 'white' }}>
                      {story.category}
                    </span>
                    <span className="text-sm" style={{ color: '#b06820' }}>
                      {story.location} • {story.date}
                    </span>
                  </div>

                  <blockquote className="text-lg md:text-xl leading-relaxed mb-6" style={{ color: '#1A1A1A' }}>
                    "{story.story}"
                  </blockquote>

                  <p className="font-serif text-xl" style={{ color: '#c07030' }}>
                    — {story.name}
                  </p>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gold/10">
                <button
                  onClick={prevStory}
                  className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gold/10 transition-colors"
                  style={{ color: '#1E3A5F' }}
                >
                  <ChevronLeft size={20} />
                  <span className="hidden sm:inline">Previous Story</span>
                </button>

                {/* Dots */}
                <div className="flex gap-2">
                  {impactStories.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentStory(i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === currentStory ? 'w-8' : ''
                      }`}
                      style={{ 
                        background: i === currentStory ? '#c07030' : '#d4956a40'
                      }}
                    />
                  ))}
                </div>

                <button
                  onClick={nextStory}
                  className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gold/10 transition-colors"
                  style={{ color: '#1E3A5F' }}
                >
                  <span className="hidden sm:inline">Next Story</span>
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* See Your Impact Section */}
      <section className="py-20" style={{ background: '#1A1A1A' }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1 rounded-full text-xs uppercase tracking-widest mb-4"
              style={{ background: 'rgba(192, 112, 48, 0.3)', color: '#d4956a', fontWeight: 600 }}>
              See Your Impact
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Every Dollar Makes a Difference
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              We stretch every donation to maximize impact. Here's exactly what your generosity provides.
            </p>
          </motion.div>

          {/* Impact Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {impactLevels.map((level, i) => (
              <motion.div
                key={level.amount}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                onClick={() => { setSelectedAmount(level.amount); setCustomAmount(''); }}
                className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-300 hover:scale-105 ${
                  selectedAmount === level.amount && !customAmount
                    ? 'ring-2 ring-gold bg-white/15'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className={`w-14 h-14 ${level.color} rounded-xl flex items-center justify-center mb-4`}>
                  <level.icon className="text-white" size={28} />
                </div>
                <div className="text-3xl font-bold text-white mb-2">
                  ${level.amount}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {level.title}
                </h3>
                <p className="text-white/60 text-sm">
                  {level.description}
                </p>
                {selectedAmount === level.amount && !customAmount && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                    <Heart className="text-white" size={14} fill="white" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Custom Amount */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-md mx-auto mb-12"
          >
            <label className="block text-white/70 text-sm mb-2 text-center">
              Or enter a custom amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gold" size={24} />
              <input
                type="number"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 border border-white/20 text-white text-xl text-center focus:outline-none focus:ring-2 focus:ring-gold placeholder:text-white/40"
              />
            </div>
          </motion.div>

          {/* Impact Calculator Result */}
          {displayAmount > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-2xl mx-auto p-8 rounded-2xl text-center mb-12"
              style={{ background: 'rgba(192, 112, 48, 0.2)', border: '1px solid rgba(212, 149, 106, 0.3)' }}
            >
              <p className="text-white/70 mb-2">Your gift of</p>
              <p className="text-5xl font-bold text-gold mb-4">${displayAmount}</p>
              <p className="text-white text-lg">
                Could feed <span className="font-bold text-gold">{impact.families} {impact.families === 1 ? 'family' : 'families'}</span> for a week
                <br />
                <span className="text-white/70">That's approximately <span className="text-gold">{impact.meals} meals</span> on the table</span>
              </p>
            </motion.div>
          )}

          {/* Donate CTA */}
          <div className="text-center">
            <Link
              to="/donate"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 hover:shadow-lg"
              style={{ background: '#c07030', color: 'white' }}
            >
              <Heart size={22} />
              Give Today
            </Link>
            <p className="text-white/50 text-sm mt-4">
              100% of donations go directly to ministry programs
            </p>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-2xl md:text-3xl mb-2" style={{ color: '#1A1A1A' }}>
              Your Trust Matters to Us
            </h2>
            <p style={{ color: '#1E3A5F' }}>
              We're committed to transparency and stewardship
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { icon: "🏛️", title: "501(c)(3)", desc: "Tax-Exempt Status" },
              { icon: "📊", title: "90%+", desc: "Goes to Programs" },
              { icon: "📋", title: "Annual Reports", desc: "Available on Request" },
              { icon: "🤝", title: "15+ Years", desc: "Serving Central Florida" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6"
              >
                <div className="text-4xl mb-3">{item.icon}</div>
                <div className="text-xl font-bold" style={{ color: '#1A1A1A' }}>{item.title}</div>
                <div className="text-sm" style={{ color: '#1E3A5F' }}>{item.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Your Story Section */}
      <section className="py-20 bg-cream" data-testid="share-story-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-1 rounded-full text-xs uppercase tracking-widest mb-4"
              style={{ background: '#c07030', color: 'white', fontWeight: 600 }}>
              Your Voice Matters
            </span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: '#1A1A1A' }}>
              Share Your Story
            </h2>
            <p style={{ color: '#1E3A5F' }} className="max-w-2xl mx-auto">
              Has Living Fruit Ministry made a difference in your life? We'd love to hear from you. 
              Your story could inspire others and show the power of community.
            </p>
          </motion.div>

          {!showStoryForm && !submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <button
                onClick={() => setShowStoryForm(true)}
                className="inline-flex items-center gap-3 px-10 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 hover:shadow-lg"
                style={{ background: '#c07030', color: 'white' }}
                data-testid="share-story-btn"
              >
                <HandHeart size={22} />
                Share My Story
              </button>
            </motion.div>
          ) : submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-12 rounded-3xl bg-white shadow-xl border border-gold/10"
            >
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#c07030' }}>
                <CheckCircle className="text-white" size={40} />
              </div>
              <h3 className="font-serif text-2xl mb-4" style={{ color: '#1A1A1A' }}>
                Thank You for Sharing!
              </h3>
              <p style={{ color: '#1E3A5F' }} className="mb-6">
                Your story has been submitted for review. Our team will carefully read it, 
                and once approved, it will appear on this page to inspire others.
              </p>
              <button
                onClick={resetForm}
                className="text-sm underline"
                style={{ color: '#c07030' }}
              >
                Submit Another Story
              </button>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleStorySubmit}
              className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gold/10"
              data-testid="share-story-form"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={storyFormData.name}
                    onChange={(e) => setStoryFormData({ ...storyFormData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                    placeholder="e.g., Maria & Family"
                    data-testid="story-name-input"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Your Location *
                  </label>
                  <input
                    type="text"
                    required
                    value={storyFormData.location}
                    onChange={(e) => setStoryFormData({ ...storyFormData, location: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                    placeholder="e.g., Kissimmee, FL"
                    data-testid="story-location-input"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Story Category *
                </label>
                <select
                  required
                  value={storyFormData.category}
                  onChange={(e) => setStoryFormData({ ...storyFormData, category: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                  style={{ color: storyFormData.category ? '#1A1A1A' : '#9ca3af' }}
                  data-testid="story-category-select"
                >
                  <option value="">Select a category...</option>
                  {storyCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Story */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Your Story *
                </label>
                <textarea
                  required
                  rows={6}
                  value={storyFormData.story}
                  onChange={(e) => setStoryFormData({ ...storyFormData, story: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                  placeholder="Tell us how Living Fruit Ministry has impacted your life..."
                  data-testid="story-text-input"
                />
              </div>

              {/* Photo Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Add a Photo (Optional)
                </label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-dashed border-gold/30 hover:border-gold/50 transition-colors">
                    <Camera size={20} style={{ color: '#c07030' }} />
                    <span className="text-sm" style={{ color: '#1E3A5F' }}>
                      {storyPhoto ? 'Change Photo' : 'Upload Photo'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      data-testid="story-photo-input"
                    />
                  </label>
                  {photoPreview && (
                    <div className="relative">
                      <img src={photoPreview} alt="Preview" className="w-16 h-16 rounded-xl object-cover" />
                      <button
                        type="button"
                        onClick={() => { setStoryPhoto(null); setPhotoPreview(null); }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs mt-2" style={{ color: '#9ca3af' }}>Max 5MB. JPG, PNG, or WebP.</p>
              </div>

              {/* Contact Info (Optional) */}
              <div className="grid md:grid-cols-2 gap-6 mb-8 p-6 rounded-xl" style={{ background: '#f7ede0' }}>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    value={storyFormData.email}
                    onChange={(e) => setStoryFormData({ ...storyFormData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 bg-white"
                    placeholder="In case we need to reach you"
                    data-testid="story-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    value={storyFormData.phone}
                    onChange={(e) => setStoryFormData({ ...storyFormData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 bg-white"
                    placeholder="(xxx) xxx-xxxx"
                    data-testid="story-phone-input"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setShowStoryForm(false)}
                  className="px-6 py-3 rounded-full text-sm font-medium transition-colors"
                  style={{ color: '#1E3A5F' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#c07030', color: 'white' }}
                  data-testid="story-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Story
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-center mt-6" style={{ color: '#9ca3af' }}>
                By submitting, you agree that your story may be published on our website after review.
              </p>
            </motion.form>
          )}
        </div>
      </section>

      {/* Final CTA - Subtle */}
      <section className="py-16" style={{ background: '#f7ede0' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-serif text-2xl md:text-3xl mb-6" style={{ color: '#1A1A1A' }}>
              "The fruit of the righteous is a tree of life, and whoever captures souls is wise."
            </p>
            <p className="text-sm uppercase tracking-widest mb-8" style={{ color: '#b06820' }}>
              — Proverbs 11:30
            </p>
            <p className="mb-8" style={{ color: '#1E3A5F' }}>
              Whether through prayer, volunteering, or giving — every act of love makes a difference.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/volunteer"
                className="px-8 py-3 rounded-full font-medium transition-all hover:scale-105 border-2"
                style={{ borderColor: '#c07030', color: '#c07030' }}
              >
                Volunteer With Us
              </Link>
              <Link
                to="/prayers"
                className="px-8 py-3 rounded-full font-medium transition-all hover:scale-105 border-2"
                style={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}
              >
                Submit Prayer Request
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
